import { Fragment, useEffect, useState } from "react";
import { LayerContent } from "../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { handlePointClicked, transformDataForBarChart } from "../formUtils";
import SectionModal, { DefaultContainer } from "./SectionModal";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

// Section 5 — Day of Week incident analysis.
// Reads woundAnalysisIncidents.dayOfWeek off the consolidated /analytic/wound-analysis payload.
const DayOfWeek = ({ setClickedIncident }: any) => {
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [day, setDay] = useState("");
  const [showDayModal, setShowDayModal] = useState(false);
  const [dayDetails, setDayDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  type DayRow = {
    day: string;
    count: number;
    percentage: number;
  };

  const adjustedTableData = (dayData: any): DayRow[] => {
    const returnData: DayRow[] = [];
    if (dayData?.length) {
      let countTotal = 0;
      let percentageTotal = 0;
      for (let i = 0; i < dayData.length; i++) {
        const row: DayRow = {
          day: dayData[i].day,
          count: dayData[i].count,
          percentage: dayData[i].percentage,
        };
        returnData.push(row);
        countTotal += dayData[i].count;
        percentageTotal += dayData[i].percentage;
      }
      const totalRow: DayRow = {
        day: "Total",
        count: countTotal,
        percentage: percentageTotal,
      };
      returnData.push(totalRow);
    }
    return returnData;
  };

  useEffect(() => {
    if (woundAnalysisIncidents !== undefined) {
      const tempData = woundAnalysisIncidents?.dayOfWeek?.dayBreakdown;
      const transformedData = adjustedTableData(tempData);
      const transformedBarData = transformDataForBarChart(transformedData, "day");
      setTableData(transformedData);
      setBarData(transformedBarData);
      setDayDetails(woundAnalysisIncidents?.allIncidents);
    }
  }, [woundAnalysisIncidents]);

  const title = "Day of Week Incident Analysis";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowDayModal(true);
    setDay(label);
    const filteredDetails =
      woundAnalysisIncidents?.dayOfWeek?.drillDownByDay?.[label] || [];
    setDayDetails(filteredDetails);
  };

  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    const newGrid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="none" rows={{ data: TotalRow }}>
          <GridColumn field="day" title="Day" />
          <GridColumn
            field="count"
            title="Count"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="Percentage"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(newGrid);
  }, [tableData]);

  return (
    <LayerContent>
      <DefaultContainer
        title={title}
        grid={grid}
        handleBarClicked={(chart: any) => handleBarClicked(chart)}
        barData={barData}
        barType="vertical"
      />
      {showDayModal ? (
        <SectionModal
          open={showDayModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowDayModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={dayDetails}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${day}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: dayDetails,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${day}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              dayDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${day}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default DayOfWeek;
